import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { useAuth } from "auth/useAuth";
import { setupApiAuth } from "../../helpers/auth-helper";
import ApiService from "../../shared/services/apiService";

// Module-level cache so data survives component unmounts
let cachedCandidates = [];
let cacheLoaded = false;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const isCacheValid = () => cacheLoaded && (Date.now() - cacheTimestamp) < CACHE_TTL;

const CandidatosContext = createContext();

export const useCandidatos = () => useContext(CandidatosContext);

export const CandidatosProvider = ({ children }) => {
  const auth = useAuth();
  const [candidates, setCandidates] = useState(cachedCandidates);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(isCacheValid());

  const fetchCandidates = useCallback(async (force = false) => {
    if (isCacheValid() && !force) {
      setCandidates(cachedCandidates);
      setLoaded(true);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setupApiAuth(auth);
      const data = await ApiService.listCandidates();
      const result = data.data || data;
      cachedCandidates = result;
      cacheLoaded = true;
      cacheTimestamp = Date.now();
      setCandidates(result);
      setLoaded(true);
    } catch (err) {
      console.error("Error fetching candidates:", err);
      setError(
        err.response?.status === 403
          ? "Acesso negado. Você não tem permissão para visualizar candidatos."
          : "Erro ao carregar candidatos."
      );
    } finally {
      setLoading(false);
    }
  }, [auth]);

  const getCandidateById = useCallback((id) => {
    return cachedCandidates.find((c) => c.id === id) || null;
  }, []);

  /** Drops a candidate from the module cache and state (after a delete). */
  const removeCandidate = useCallback((id) => {
    cachedCandidates = cachedCandidates.filter((c) => c.id !== id);
    setCandidates(cachedCandidates);
  }, []);

  /** Forces the next fetch to hit the API. */
  const invalidate = useCallback(() => {
    cacheLoaded = false;
    cacheTimestamp = 0;
  }, []);

  return (
    <CandidatosContext.Provider value={{ candidates, loading, error, loaded, fetchCandidates, getCandidateById, removeCandidate, invalidate }}>
      {children}
    </CandidatosContext.Provider>
  );
};
